import { myLocalStorage } from "../../../StorageHelper";
import {
  getAllAzureClusters,
  getAllAzureDatabase,
  getAllAzureResources,
  getAllAzureStorage,
  getAWSAllEC2,
  getAWSAllRDS,
  getAWSAllS3,
  getAWSClusters,
  getClusterTabs,
  getGCPAllDatabases,
  getGCPAllStorages,
  getGCPAllVms,
  getGCPCluster,
  publishAzureResource,
  publishGCPBucket,
  publishGCPDatabase,
  publishGCPVMS,
  publishK8Resource,
  publishAWSEC2Instance,
  publishAWSS3Bucket,
  publishAWSRDSInstance
} from "./Api/resourceapi";

export interface Resource {
  id: string;
  name: string;
  type: "vm" | "storage" | "database";
  project?: string;
  region: string;
  createdAt: string;
  status: "running" | "stopped" | "available";
  description: string;
}

export interface UserProjectAccess {
  userName: string;
  projectId: string;
  accountEmail: string;
  syncedAt: string;
  roles: string[];
}

export const getResourceTypes = async (
  clusterId: string,
  type: string,
  cloudProviderType: string,
  namespace: any
) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

  const payload = {
    wsTenantName: latestTenant,
    clusterId,
    cloudProviderType: cloudProviderType?.toUpperCase(),
    type: type?.toUpperCase(),
    namespace,
  };

  const response = await getClusterTabs(payload, "NAMESPACE");

  const transformed = response.map((item: any, index: number) => ({
    id: `deploy-${item.id || index + 1}`,
    name: item.name || "unknown",
    type: "namespace",
    cluster: item.clusterId?.split("/").pop() || "unknown-cluster",
    namespace: item.namespace || "default",
    createdAt: item.creationTimestamp
      ? new Date(item.creationTimestamp).toISOString().split("T")[0]
      : "N/A",
    status: item.published ? "running" : "stopped",
    details: item,
    uid: item.uid,
    published: item.published
  }));

  return transformed;
};

export const viewTabDetails = async (
  clusterId: string,
  type: string,
  cloudProviderType: string,
  namespace: any
) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

  const payload = {
    wsTenantName: latestTenant,
    clusterId,
    cloudProviderType: cloudProviderType?.toUpperCase(),
    type: type?.toUpperCase(),
    namespace,
  };

  const response = await getClusterTabs(payload, "CLUSTER");
  return response;
};

export const getGCPVMList = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  const response = await getGCPAllVms(latestTenant, latestProjectId);

  const converted = (response || []).map((item: any) => ({
    id: item.id,
    name: item.name || "-",
    type: "vm",
    project: item.projectId || "-",
    region: item.zone || "-",
    createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-GB") : "-",
    status: (item.status || "UNKNOWN").toLowerCase(),
    description: "Google Cloud Virtual Machine",
    vmdetails: item,
    publish: item.isManagedVirtualMachine,
    activity: item.activity
  }));

  return converted;
};

export const getGCPStorageList = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  const response = await getGCPAllStorages(latestTenant, latestProjectId);

  const converted = (response || []).map((item: any) => ({
    id: item.id,
    name: item.name || "-",
    type: "storage",
    project: item.projectId || "-",
    region: item.location?.toLowerCase() || "-",
    createdAt: item.createdTime ? new Date(Number(item.createdTime)).toLocaleDateString("en-GB") : "-",
    status: "available",
    description: `Google Cloud Storage (${item.storageClass || "STANDARD"})`,
    storagedetails: item,
    publish: item.isManagedStorageBucket,
    activity: item.activity
  }));

  return converted;
};

export const getGCPDatabaseList = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  const response = await getGCPAllDatabases(latestTenant, latestProjectId);

  const converted = (response || []).map((item: any) => ({
    id: item.id,
    name: item.name || "-",
    type: "database",
    project: item.projectId || "-",
    region: item.region || "-",
    createdAt: "-",
    status: item.state?.toLowerCase() || "-",
    description: `Cloud SQL ${item.databaseVersion?.replace("_", " ") || "Database Instance"}`,
    databasedetails: item,
    publish: item.isManagedDatabase,
    activity: item.activity
  }));

  return converted;
};

export function getGCPUserProjectAccess(project: string): UserProjectAccess | null {
  const access: Record<string, UserProjectAccess> = {
    "whiteswan-vm-allocation": {
      userName: "Phaneendra",
      projectId: project,
      accountEmail: "phaneendra@whiteswansecurity.com",
      syncedAt: "07/07/2025, 15:07:16",
      roles: ["roles/owner", "roles/editor", "roles/viewer", "roles/compute.admin", "roles/storage.admin"],
    },
    "production-analytics": {
      userName: "Phaneendra",
      projectId: project,
      accountEmail: "phaneendra@whiteswansecurity.com",
      syncedAt: "08/07/2025, 10:30:22",
      roles: ["roles/editor", "roles/viewer"],
    },
    "dev-environment": {
      userName: "Phaneendra",
      projectId: project,
      accountEmail: "phaneendra@whiteswansecurity.com",
      syncedAt: "09/07/2025, 09:15:45",
      roles: ["roles/viewer"],
    },
  };
  return access[project] || null;
}

export const getAWSEC2List = async (region?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  const response = await getAWSAllEC2(latestTenant, selectedAwsAccount);

  if (!response || response.length === 0) return [];

  return response.map((item: any, index: number) => ({
    id: `aws-vm-${index + 1}`,
    name: item.instanceName || item.instanceId,
    type: "ec2",
    region: item.availabilityZone?.replace(/[a-z]$/, "") || "unknown",
    createdAt: item.launchTime ? new Date(item.launchTime).toLocaleDateString("en-GB") : "unknown",
    status: item.instanceState || "unknown",
    description: "Amazon EC2 Instance",
    ec2details: item,
    publish: item.isManageInstance,
    activity: item.activity
  }));
};

export const getAWSS3List = async (region?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  const response = await getAWSAllS3(latestTenant, selectedAwsAccount);

  if (!response || response.length === 0) return [];

  return response.map((item: any, index: number) => ({
    id: `aws-storage-${index + 1}`,
    name: item.bucketName,
    type: "s3",
    region: item.region || "unknown",
    createdAt: item.creationDate ? new Date(item.creationDate).toLocaleDateString("en-GB") : "unknown",
    status: "available",
    description: "Amazon S3 Bucket",
    s3details: item,
    publish: item.isManagedS3,
    activity: item.activity
  }));
};

export const getAWSRDSList = async (region?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  const response = await getAWSAllRDS(latestTenant, selectedAwsAccount);

  if (!response || response.length === 0) return [];

  return response.map((item: any, index: number) => ({
    id: `aws-db-${index + 1}`,
    name: item.dbInstanceIdentifier || item.dbClusterIdentifier || `rds-instance-${index + 1}`,
    type: "rds",
    region: item.availabilityZone?.replace(/[a-z]$/, "") || item.region || "unknown",
    createdAt: item.instanceCreateTime ? new Date(item.instanceCreateTime).toLocaleDateString("en-GB") : "unknown",
    status: item.dbInstanceStatus || item.status || "unknown",
    description: getRDSDescription(item.engine),
    publish: item.isManagedRDS,
    activity: item.activity
  }));
};

const getRDSDescription = (engine?: string): string => {
  if (!engine) return "Amazon RDS Instance";

  const engineLower = engine.toLowerCase();

  if (engineLower.includes("postgres")) return "Amazon RDS PostgreSQL";
  if (engineLower.includes("mysql")) return "Amazon RDS MySQL";
  if (engineLower.includes("mariadb")) return "Amazon RDS MariaDB";
  if (engineLower.includes("oracle")) return "Amazon RDS Oracle";
  if (engineLower.includes("sqlserver")) return "Amazon RDS SQL Server";
  if (engineLower.includes("aurora")) return "Amazon Aurora Database";
  if (engineLower.includes("redshift")) return "Amazon Redshift Cluster";
  if (engineLower.includes("redis")) return "Amazon ElastiCache Redis";

  return "Amazon RDS Instance";
};

export const getAzureVMList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  const response = await getAllAzureResources(latestTenant, subId);

  const azureVMs =
    response
      ?.filter((r: any) => r.resourceType === "Microsoft.Compute/virtualMachines")
      ?.map((vm: any) => {
        const createdDate = vm.syncedAt ? new Date(vm.syncedAt).toLocaleDateString("en-GB") : "N/A";

        return {
          id: vm.azureVmId,
          name: vm.name || "Unknown VM",
          type: "vm",
          region: vm.region || "unknown",
          createdAt: createdDate,
          status: vm.powerState?.replace("PowerState/", "") || "unknown",
          description: "Azure Virtual Machine",
          vmDetails: vm,
          publish: vm.published,
          instanceId: vm.azureVmId,
          activity: vm.activity
        };
      }) || [];

  return azureVMs;
};

export const getAzureStorageList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  const response = await getAllAzureStorage(latestTenant, subId);

  const azureStorageAccounts =
    response
      ?.filter((r: any) => r.resourceType === "Microsoft.Storage/storageAccounts")
      ?.map((storage: any) => {
        const createdDate = storage.createdDate ? new Date(storage.createdDate).toLocaleDateString("en-GB") : "N/A";
        const isBlob = storage.kind?.toLowerCase().includes("blob");
        const description = isBlob ? "Azure Blob Storage" : "Azure Storage Account";

        return {
          id: storage.id,
          name: storage.storageAccountName || "Unknown Storage",
          type: "storage",
          region: storage.region || "unknown",
          createdAt: createdDate,
          status: storage.publicAccess === "Enabled" ? "available" : "restricted",
          description,
          storagedetails: storage,
          publish: storage.published,
          instanceId: storage.azureStorageAccountId,
          activity: storage.activity
        };
      }) || [];

  return azureStorageAccounts;
};

export const getAzureDatabaseList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  const response = await getAllAzureDatabase(latestTenant, subId);

  const formattedList = Array.isArray(response)
    ? response.map((db: any, index: number) => ({
      id: `azure-db-${index + 1}`,
      name: db.databaseName || db.name || "unknown-database",
      type: "database",
      region: db.region || db.location || "unknown-region",
      createdAt: db.createdDate ? new Date(db.createdDate).toLocaleDateString("en-GB") : "unknown-date",
      status: db.status || db.state || "running",
      description:
        db.resourceType?.includes("Cosmos")
          ? "Azure Cosmos DB"
          : db.resourceType?.includes("postgres")
            ? "Azure Database for PostgreSQL"
            : db.resourceType?.includes("sql")
              ? "Azure SQL Database"
              : "Azure Database",
      publish: db.published,
      activity: db.activity
    }))
    : [];

  return formattedList;
};

export const awsClusters = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  const response = await getAWSClusters(latestTenant, selectedAwsAccount);

  const converted =
    Array.isArray(response)
      ? response.map((item: any) => ({
        id: item.id,
        name: item.clusterName || "-",
        region: item.region || "-",
        groupName: item.resourceGroupName || "-",
        type: item.clusterType || "-",
        published: item.published
      }))
      : [];

  return converted;
};

export const gcpClusters = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  const response = await getGCPCluster(latestTenant, latestProjectId);

  const converted =
    Array.isArray(response)
      ? response.map((item: any) => ({
        id: item.gcpClusterId,
        name: item.name || "-",
        region: item.location || "-",
        groupName: item.resourceGroupName || "-",
        type: item.clusterType || "-",
        published: item.isManagedKubernetesCluster,
        uid: item.uid
      }))
      : [];

  return converted;
};

export const azureClusters = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  const response = await getAllAzureClusters(latestTenant, subId);

  const converted =
    Array.isArray(response)
      ? response.map((item: any) => ({
        id: item.azureId,
        name: item.name || "-",
        region: item.regionName || "-",
        groupName: item.resourceGroupName || "-",
        type: item.type?.split("/").pop() || "-",
        published: item.published,
        uid: item.uid
      }))
      : [];

  return converted;
};

export const updateGCPResourcePublish = async (type: string, id: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  if (type === "vm") {
    await publishGCPVMS(latestTenant, latestProjectId, id);
  } else if (type === "storage") {
    await publishGCPBucket(latestTenant, latestProjectId, id);
  } else if (type === "database") {
    await publishGCPDatabase(latestTenant, latestProjectId, id);
  }
};

export const updateAWSResourcePublish = async (type: string, resource: any) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  if (type === "ec2") {
    await publishAWSEC2Instance(latestTenant, selectedAwsAccount, resource.arn);
  } else if (type === "s3") {
    await publishAWSS3Bucket(latestTenant, selectedAwsAccount, resource.name);
  } else if (type === "rds") {
    await publishAWSRDSInstance(latestTenant, selectedAwsAccount, resource.arn);
  }
};

export const updateAzureResourcePublish = async (payload: any) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  let newPayload = {
    ...payload,
    wsTenantName: latestTenant,
    resourceAccountId: subId
  };
  await publishAzureResource(newPayload);
};

export const updateK8ResourcePublish = async (payload: any, cloudProviderType: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  let accountId = cloudProviderType === "aws" ? selectedAwsAccount : cloudProviderType === "gcp" ? latestProjectId : subId;
  let newPayload = {
    ...payload,
    wsTenantName: latestTenant,
    resourceAccountId: accountId
  };
  await publishK8Resource(newPayload);
};